import { Inject, Injectable } from '@angular/core';
import { from, map, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { BaseService } from 'src/app/core/services/base.httpService';
import { URLConstants } from 'src/app/shared/utility/url-constants';
import {
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { loginRequest } from '../../auth-config';
import { AccountInfo, InteractionType } from '@azure/msal-browser';
import { ModalPopupMessages, popupWidth, STORAGE_KEYS } from '../../shared/utility/constants';
import { MessagePopupComponent } from '../../shared/components/message-popup/message-popup.component';
import { ModalService } from '../../shared/services/modal.service';
import { UserEffectivePermissionsService } from '../../features/service/user-effective-permissions.service';
import { LocalStorageKeyService } from '../../shared/services/local-storage-key.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class IBCAuthService {
  private warningPopup = false;
  private cachedIpAddress?: string;

  constructor(
    public baseService: BaseService,
    private readonly authService: MsalService,
    private readonly localStorageService: LocalStorageService,
    @Inject(MSAL_GUARD_CONFIG)
    private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly modalService: ModalService,
    private readonly userEffectivePermissionsService: UserEffectivePermissionsService,
    private readonly localStorageKeyService: LocalStorageKeyService
  ) {}

  refreshSession() {
    this.tryInitLocalStorageEncryptionKey();

    let currentUserAccount: AccountInfo | null = null;

    try {
      const loggedInUserName = this.getTokenInfo()?.username;
      if (loggedInUserName) {
        currentUserAccount =
          this.authService.instance.getAccountByUsername(loggedInUserName);
      }

      currentUserAccount ??= this.authService.instance.getActiveAccount();

      if (
        !currentUserAccount &&
        this.authService.instance.getAllAccounts().length > 0
      ) {
        currentUserAccount = this.authService.instance.getAllAccounts()[0];
        this.authService.instance.setActiveAccount(currentUserAccount);
      }
    } catch {
      return of(null);
    }

    if (!currentUserAccount) {
      return of(null);
    }

    this.localStorageKeyService.initializeFromAccount(
      currentUserAccount,
      environment.clientId
    );

    const request = {
      ...loginRequest,
      account: currentUserAccount,
    };

    const ipAddress$ = this.cachedIpAddress
      ? of({ ip: this.cachedIpAddress })
      : this.getIpAddressOfUSer();

    return from(this.authService.instance.acquireTokenSilent(request)).pipe(
      switchMap(response =>
        ipAddress$.pipe(
          map(({ ip }) => ({
            ip,
            accessToken: response?.accessToken,
          }))
        )
      ),
      switchMap(response => {
        this.cachedIpAddress = response.ip;
        return this.authorizeB2CToken(response.accessToken ?? '', response.ip);
      }),
      tap(({ accessToken }) => this.setJWTToken(accessToken)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  getJWTToken(): string | null {
    return this.localStorageService.getItem(STORAGE_KEYS.JWT_TOKEN);
  }

  setJWTToken(token: string): void {
    this.localStorageService.setItem(STORAGE_KEYS.JWT_TOKEN, token);
  }

  getTokenInfo(): AccountInfo | null {
    return this.localStorageService.getItem(STORAGE_KEYS.TOKEN_INFO);
  }

  setTokenInfo(info: AccountInfo): void {
    this.localStorageKeyService.initializeFromAccount(info, environment.clientId);
    this.localStorageService.setItem(STORAGE_KEYS.TOKEN_INFO, info);
  }

  public tryInitLocalStorageEncryptionKey(): boolean {
    return this.localStorageKeyService.initializeFromMsalInstance(
      this.authService.instance,
      environment.clientId
    );
  }

  showWarningPopupAndLogout(
    message = ModalPopupMessages.sessionExpiredMessage
  ) {
    if (this.warningPopup) {
      return;
    }

    const dialogRef = this.modalService.openPopup(
      {
        isFooterRequired: true,
        isForWarning: true,
        message,
      },
      '',
      MessagePopupComponent,
      popupWidth.warningPopup
    );
    this.warningPopup = true;
    dialogRef.afterClosed().pipe(take(1)).subscribe(data => {
      if (data === true) {
        this.initiateLogout();
      }
    });
  }

  initiateLogout(): void {
    const activeAccount =
      this.authService.instance.getActiveAccount() ??
      this.authService.instance.getAllAccounts()[0];

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.authService.logoutPopup({
        account: activeAccount,
      });
    } else {
      this.authService.logoutRedirect({
        account: activeAccount,
      });
    }
    this.clearState();
  }

  endSessionSilent() {
    this.authService.logoutRedirect({
      onRedirectNavigate: () => false,
    });
    this.clearState();
  }

  private clearState() {
    this.cachedIpAddress = undefined;
    this.warningPopup = false;
    this.userEffectivePermissionsService.clearPermissions();
    this.localStorageKeyService.clear();
    this.localStorageService.clear();
    sessionStorage.clear();
  }

  public authorizeB2CToken(
    accessToken: string,
    ipAddress: string
  ): Observable<any> {
    const url = URLConstants.authorize;
    return this.baseService.authorizeToken(url, accessToken, ipAddress);
  }

  public getIpAddressOfUSer(): Observable<any> {
    const url = URLConstants.ipAddress;
    return this.baseService.ipAddress(url);
  }
}
